import { useQuery, useQueryClient } from 'react-query'

import { fetchAccounts, fetchConnection, fetchConnections } from '../service/adminConnectionsService'
import { ACTIVE_RUN_STATUSES, RosterConnectionDetail } from '../types'

const CONNECTIONS_QUERY_PREFIX = ['admin-connections', 'list']
const RUN_POLL_INTERVAL_MS = 5000
export const ACCOUNTS_PAGE_SIZE = 25

export function useConnections() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery(CONNECTIONS_QUERY_PREFIX, fetchConnections, {
    refetchOnWindowFocus: false,
    retry: 1,
  })

  const refetchConnections = () => queryClient.invalidateQueries(CONNECTIONS_QUERY_PREFIX)

  return {
    connections: data ?? [],
    isLoading,
    isError,
    refetchConnections,
  }
}

/**
 * Connection detail with recent runs. While any run is pending/processing the
 * query polls so the screen follows the sync progress live.
 */
export function useConnectionDetail(connectionId: string | null) {
  const queryClient = useQueryClient()
  const queryKey = ['admin-connections', 'connection', connectionId]

  const { data, isLoading, isError } = useQuery<RosterConnectionDetail>(
    queryKey,
    () => fetchConnection(connectionId as string),
    {
      enabled: Boolean(connectionId),
      refetchOnWindowFocus: false,
      retry: 1,
      refetchInterval: detail => {
        const hasActiveRun = detail?.recentRuns?.some(run =>
          ACTIVE_RUN_STATUSES.includes(run.status),
        )
        return hasActiveRun ? RUN_POLL_INTERVAL_MS : false
      },
    },
  )

  const refetchConnection = () => queryClient.invalidateQueries(queryKey)

  return { connection: data, isLoading, isError, refetchConnection }
}

/** Read-only list of the accounts a connection's sync created (one per school). */
export function useConnectionAccounts(connectionId: string | null, page: number) {
  const { data, isLoading, isError } = useQuery(
    ['admin-connections', 'accounts', connectionId, page],
    () =>
      fetchAccounts({
        page,
        limit: ACCOUNTS_PAGE_SIZE,
        rosterConnectionId: connectionId as string,
      }),
    {
      enabled: Boolean(connectionId),
      refetchOnWindowFocus: false,
      retry: 1,
      keepPreviousData: true,
    },
  )

  return {
    accounts: data?.accounts ?? [],
    pagination: data?.pagination ?? null,
    isLoading,
    isError,
  }
}
